import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Box, Column, Divider, Row, SmartTooltip, Text } from '~/commonComponents';
import { JobPostingStatus, JobPostingStatusMapper } from '~/domain/PermanentJobs';
import { useJobProfessionDisplay } from '~/hooks';
import { WarningSolidIcon } from '~/images/icons';
import { Colors } from '~/themes/colors';
import { Badge } from '../../../components';
import { TJobCardProps } from './Types';

export const JobCard = ({
  closedAt,
  hires,
  id,
  office,
  onsiteInterviewsCount,
  professionType,
  publishedAt,
  status,
  subProfession,
  title,
}: TJobCardProps) => {
  const navigate = useNavigate();

  const fmtTimestamp = useMemo(() => {
    if (status === JobPostingStatus.Closed) {
      return `Closed on ${closedAt}`;
    }

    return `Posted on ${publishedAt}`;
  }, [closedAt, publishedAt, status]);

  const hasNoInterviewSlots = onsiteInterviewsCount === 0;

  const isActiveJob = status === JobPostingStatus.Active;
  const isSuspendedJob = status === JobPostingStatus.Suspended;

  const isShowInterviewCounter =
    isActiveJob && onsiteInterviewsCount !== undefined && onsiteInterviewsCount <= 3;
  const totalHires = hires ?? 0;

  const professionTypeDisplay = useJobProfessionDisplay({
    profession: professionType,
    subProfession,
  });

  return (
    <Row
      alignItems="center"
      backgroundColor={Colors.white}
      borderRadius={10}
      boxShadow="0 1px 2px 0 rgba(0, 0, 0, 0.15), 0 1px 3px 1px rgba(0, 0, 0, 0.15)"
      display="grid"
      gridTemplateColumns="1fr 25% 20%"
      key={id}
      paddingBlock={12}
    >
      <Column gap={6} marginLeft={36}>
        <Row alignItems="center" gap={8}>
          <Text
            bold
            textDecoration="underline"
            onClick={() => navigate(`/permanent-jobs/job-overview/${id}`)}
            style={{ cursor: 'pointer' }}
          >
            {title}
          </Text>
          {isSuspendedJob && (
            <SmartTooltip
              content="Awaiting Payment"
              hideArrow
              position="bottom"
              style={{
                Content: { fontSize: 16, paddingBlock: 1, paddingInline: 10 },
                Portal: { borderRadius: 3 },
              }}
            >
              <WarningSolidIcon color={Colors.warning_500} size={18} />
            </SmartTooltip>
          )}
        </Row>
        <Text>{office?.name}</Text>
        <Row alignItems="center" gap={12}>
          <Text>{professionTypeDisplay}</Text>
          <Divider width={1} height={12} marginVertical={0} />
          <Text color={Colors.neutral_400} fontSize={14} italic>
            {fmtTimestamp}
          </Text>
          {isShowInterviewCounter && (
            <Badge
              label={`${onsiteInterviewsCount} INTERVIEWS LEFT`}
              variant={hasNoInterviewSlots ? 'error' : 'warning'}
            />
          )}
        </Row>
      </Column>
      <Text
        bold
        color={totalHires > 0 ? Colors.neutral_600 : Colors.neutral_300}
        fontSize={18}
        textAlign="center"
      >
        {totalHires}
      </Text>
      <Row alignItems="center" gap={8}>
        <Box
          backgroundColor={JobPostingStatusMapper.getColor(status)}
          borderRadius="100%"
          height={10}
          width={10}
        />
        <Text>{JobPostingStatusMapper.toHumanReadable(status)}</Text>
      </Row>
    </Row>
  );
};
